import React from "react";

import {
  createVisitorRow,
  isVisitorRowEmpty,
  VISIT_TYPE_OPTIONS,
  VISITOR_TYPE_OPTIONS,
} from "./reportingConfig";
import { addVisitorsApi } from "../../../services/staffReporting";

const inputClass =
  "w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";

// Returns the rows that would be saved, or throws with a readable message
// when a filled row is missing its Type / Name.
export const pendingVisitorRows = (rows) => {
  const kept = (Array.isArray(rows) ? rows : []).filter(
    (r) => !isVisitorRowEmpty(r),
  );
  const bad = kept.findIndex((r) => !r.visitorType || !String(r.name).trim());
  if (bad !== -1) {
    throw new Error(
      `Visitor row ${bad + 1}: select CP / Customer and enter a name.`,
    );
  }
  return kept;
};

// POST the filled rows for one working date. Resolves to the saved visitors
// ([] when there was nothing to save).
export const saveVisitorRows = async ({ employeeId, date, rows }) => {
  const kept = pendingVisitorRows(rows);
  if (!kept.length) return [];
  const res = await addVisitorsApi({
    ...(employeeId ? { employeeId } : {}),
    date,
    rows: kept.map(({ visitorType, visitType, name, phone, remarks }) => ({
      visitorType,
      visitType,
      name,
      phone,
      remarks,
    })),
  });
  return Array.isArray(res?.data?.visitors) ? res.data.visitors : [];
};

// Real Estate Sales — "who visited" rows: Type (CP / Customer), Visit Type,
// Name, Phone, Remarks, with + Add Row. Controlled: the parent owns `rows`.
const VisitorRowsEditor = ({ rows, onChange, onSave, saving, disabled }) => {
  const list = Array.isArray(rows) ? rows : [];
  const update = (id, key, value) =>
    onChange(list.map((r) => (r.id === id ? { ...r, [key]: value } : r)));
  const remove = (id) => onChange(list.filter((r) => r.id !== id));
  const add = () => onChange([...list, createVisitorRow()]);

  return (
    <div className="space-y-3">
      {list.map((row, index) => (
        <div
          key={row.id}
          className="grid gap-3 rounded-2xl border border-gray-200 bg-gray-50 p-3 sm:grid-cols-2 lg:grid-cols-[130px_140px_1fr_160px_1.4fr_auto]"
        >
          <select
            className={inputClass}
            value={row.visitorType}
            disabled={disabled}
            onChange={(e) => update(row.id, "visitorType", e.target.value)}
            aria-label={`Row ${index + 1} type`}
          >
            <option value="">CP / Customer</option>
            {VISITOR_TYPE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt.toUpperCase()}
              </option>
            ))}
          </select>
          <select
            className={inputClass}
            value={row.visitType}
            disabled={disabled}
            onChange={(e) => update(row.id, "visitType", e.target.value)}
            aria-label={`Row ${index + 1} visit type`}
          >
            <option value="">Visit Type</option>
            {VISIT_TYPE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          <input
            className={inputClass}
            autoComplete="off"
            placeholder="Name"
            value={row.name}
            disabled={disabled}
            onChange={(e) => update(row.id, "name", e.target.value)}
          />
          <input
            className={inputClass}
            autoComplete="off"
            inputMode="tel"
            placeholder="Phone"
            value={row.phone}
            disabled={disabled}
            onChange={(e) =>
              update(row.id, "phone", e.target.value.replace(/[^0-9+\s-]/g, ""))
            }
          />
          <input
            className={inputClass}
            autoComplete="off"
            placeholder="Remarks"
            value={row.remarks}
            disabled={disabled}
            onChange={(e) => update(row.id, "remarks", e.target.value)}
          />
          <button
            type="button"
            className="rounded-xl border border-rose-200 px-3 py-2 text-xs font-semibold text-rose-600 transition hover:bg-rose-50 disabled:opacity-40"
            onClick={() => remove(row.id)}
            disabled={disabled}
            title="Remove row"
          >
            ✕
          </button>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="rounded-xl border-2 border-amber-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-gray-600 transition hover:text-amber-700 disabled:opacity-40"
          onClick={add}
          disabled={disabled}
        >
          + Add Row
        </button>
        {onSave && list.length > 0 && (
          <button
            type="button"
            className="rounded-xl bg-amber-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
            onClick={onSave}
            disabled={disabled || saving}
          >
            {saving ? "Saving…" : "Save Visitors"}
          </button>
        )}
      </div>
    </div>
  );
};

export default VisitorRowsEditor;
